import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BaseLayout, SkeletonLoader, SelectDropdown } from '@/components';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Card,
  CardContent,
} from '@/components/ui';
import { UserForm } from '../../../../components/forms/user-form';
import {
  readUser,
  deleteUser,
  deactivateUser,
  reactivateUser,
} from '../../../../api/user.api';
import { getManagers } from '../../../../api/user.api';
import type { User, Manager } from '../../../../types/user.types';
import { useToast } from '../../../../contexts/toast-context';
import { TOAST_TYPES } from '../../../../constants/common.constants';
import { FORM_MODES } from '../../../../constants/form.constants';
import { useAuth } from '../../../../hooks/use-auth';
import { Alert } from '../../../../components';
import { ALERT_TYPES } from '../../../../constants/common.constants';
import ROLES from '../../../../constants/roles.constants';
import { AuthError } from '../../../../utils/errors';

export const Route = createFileRoute('/organizations/$uuid/edit/')({
  component: EditUser,
});

function EditUser() {
  const { uuid } = Route.useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingActivation, setIsLoadingActivation] = useState(false);
  const [isLoadingDeletion, setIsLoadingDeletion] = useState(false);

  // Modal states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);

  // Deactivation modal states
  const [managers, setManagers] = useState<Manager[]>([]);
  const [selectedManagerUuid, setSelectedManagerUuid] = useState<string>('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setIsLoading(true);
        const data = await readUser(uuid);
        setUser(data.user);

        // Fetch managers for deactivation modal if user is a manager with drivers
        if (data.user.role === ROLES.MANAGER && data.user.hasDrivers) {
          const managersData = await getManagers();
          // Filter out the current user and filter by account
          const filteredManagers = managersData.managers.filter(
            (m: Manager) =>
              m.uuid !== uuid && m.accountUuid === data.user.accountUuid,
          );
          setManagers(filteredManagers);
        }
      } catch (error) {
        addToast(`Failed to fetch user profile`, {
          type: TOAST_TYPES.ERROR,
          title: 'Fetch Error',
        });
        if (!(error instanceof AuthError)) {
          navigate({ to: '/organizations' });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [uuid]);

  const handleSuccess = () => {
    navigate({ to: `/organizations/${uuid}` });
  };

  const handleCancel = () => {
    navigate({ to: `/organizations/${uuid}` });
  };

  // Permission checks
  const canDelete = () => {
    return currentUser?.role === ROLES.ADMINISTRATOR;
  };

  const canDeactivate = () => {
    if (!currentUser || !user) return false;
    if (currentUser.role === ROLES.ADMINISTRATOR) return true;
    if (currentUser.role === ROLES.EXECUTIVE) {
      return currentUser.account === user.accountName;
    }
    return false;
  };

  const canReactivate = () => {
    if (!currentUser || !user) return false;
    if (currentUser.role === ROLES.ADMINISTRATOR) return true;
    if (currentUser.role === ROLES.EXECUTIVE) {
      return currentUser.account === user.accountName;
    }
    return false;
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeactivateClick = () => {
    setShowDeactivateDialog(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
  };

  const handleCancelDeactivate = () => {
    setShowDeactivateDialog(false);
    setSelectedManagerUuid('');
  };

  const handleReactivateClick = () => {
    setShowReactivateDialog(true);
  };

  const handleCancelReactivate = () => {
    setShowReactivateDialog(false);
  };

  const handleConfirmReactivate = async () => {
    try {
      setIsLoadingActivation(true);
      setShowReactivateDialog(false);

      await reactivateUser(uuid);

      addToast('User reactivated successfully', {
        type: TOAST_TYPES.SUCCESS,
        title: 'Success',
      });

      navigate({ to: `/organizations/${uuid}` });
    } catch (error: any) {
      addToast(`${error}`, {
        type: TOAST_TYPES.ERROR,
        title: 'Reactivate Error',
      });
      setIsLoadingActivation(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setIsLoadingDeletion(true);
      setShowDeleteDialog(false);

      await deleteUser(uuid);

      addToast('User deleted successfully', {
        type: TOAST_TYPES.SUCCESS,
        title: 'Success',
      });

      navigate({ to: '/organizations' });
    } catch (error: any) {
      addToast(`${error}`, {
        type: TOAST_TYPES.ERROR,
        title: 'Delete Error',
      });
      setIsLoadingDeletion(false);
    }
  };

  const handleConfirmDeactivate = async () => {
    try {
      setIsLoadingActivation(true);
      setShowDeactivateDialog(false);

      // Call deactivate with optional manager reassignment
      const result = await deactivateUser(uuid, {
        newManagerUuid: selectedManagerUuid || undefined,
      });

      addToast('User deactivated successfully', {
        type: TOAST_TYPES.SUCCESS,
        title: 'Success',
      });

      // If user was in hierarchy, navigate to hierarchy page
      if (result.hierarchyNode) {
        addToast(
          `User was assigned to hierarchy node: ${result.hierarchyNode.name}. You may need to reassign in the hierarchy.`,
          {
            type: TOAST_TYPES.INFO,
          },
        );
      }

      navigate({ to: `/organizations/${uuid}` });
    } catch (error: any) {
      addToast(`${error}`, {
        type: TOAST_TYPES.ERROR,
        title: 'Deactivate Error',
      });
      setIsLoadingActivation(false);
    }
  };

  if (isLoading) {
    return (
      <BaseLayout
        header={{
          headerText: 'Edit User',
        }}
        breadcrumbs={{
          showBreadcrumbs: true,
          breadcrumbText: ['Users', 'Edit User'],
        }}
      >
        <Card>
          <CardContent>
            <SkeletonLoader count={10} height={30} />
          </CardContent>
        </Card>
      </BaseLayout>
    );
  }

  if (!user) {
    return null;
  }

  // Check if user is editing their own profile
  const isOwnProfile = currentUser?.uuid === uuid;

  // Delete button: show for admins regardless of status
  const showDeleteButton = canDelete();
  // Deactivate button: show when user is activated (but not for own profile)
  const showDeactivateButton =
    canDeactivate() && user.status === 'activated' && !isOwnProfile;
  // Reactivate button: show when user is deactivated (but not for own profile)
  const showReactivateButton =
    canReactivate() && user.status === 'deactivated' && !isOwnProfile;

  return (
    <>
      <BaseLayout
        header={{
          headerText: 'Edit User',
        }}
        breadcrumbs={{
          showBreadcrumbs: true,
          breadcrumbText: [user.name],
        }}
      >
        <UserForm
          mode={FORM_MODES.EDIT}
          initialData={user}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          userStatus={user.status}
          isRoleEditable={user.isRoleEditable}
          onDeleteClick={handleDeleteClick}
          onDeactivateClick={handleDeactivateClick}
          onReactivateClick={handleReactivateClick}
          showDeleteLink={showDeleteButton}
          showDeactivateLink={showDeactivateButton}
          showReactivateLink={showReactivateButton}
          isLoadingActivation={isLoadingActivation}
          isLoadingDeletion={isLoadingDeletion}
        />
      </BaseLayout>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription className="mt-2">
              {user.isDeletable ? (
                <>
                  Are you sure you want to delete this user? This action cannot
                  be undone and will permanently remove all user data.
                </>
              ) : (
                <Alert
                  type="warning"
                  text={
                    <div>
                      <p className="mb-2">
                        This user cannot be deleted for the following reasons:
                      </p>
                      <ul className="list-disc pl-5">
                        {user.deletabilityReasons?.map((reason, index) => (
                          <li key={index}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  }
                />
              )}
            </DialogDescription>
          </DialogHeader>
          {user.isDeletable && (
            <DialogFooter>
              <Button
                variant="outline-secondary"
                size="medium"
                onClick={handleCancelDelete}
              >
                Cancel
              </Button>
              {user.isDeletable && (
                <Button
                  variant="outline-danger"
                  size="medium"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={showDeactivateDialog}
        onOpenChange={setShowDeactivateDialog}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Deactivate User</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Hierarchy Warning Section:
                Displays a warning if the user is assigned to an organization hierarchy node.
                This alerts the admin that hierarchy reassignment may be needed after deactivation. */}
            {user.organizationLevelUuid && user.role !== ROLES.DRIVER && (
              <Alert
                type={ALERT_TYPES.WARNING}
                text={
                  user.role === ROLES.STAFF
                    ? `This user is assigned as a staff member on hierarchy node: ${user.organizationLevel}. You may need to reassign that after deactivation.`
                    : `This user is assigned to organization hierarchy node: ${user.organizationLevel}. You may need to reassign this hierarchy node after deactivation.`
                }
              />
            )}

            {/* Manager Reassignment Section:
                Only shown when deactivating a manager who has drivers assigned.
                Allows the admin to optionally reassign the manager's drivers to another manager
                within the same account before deactivation. */}

            {user.role === ROLES.MANAGER &&
              user.hasDrivers &&
              managers.length > 0 && (
                <div className="space-y-2">
                  <Alert
                    type={ALERT_TYPES.INFO}
                    text="This manager has drivers assigned. You can optionally reassign them to another manager."
                  />
                  <Label>Reassign drivers to:</Label>
                  <SelectDropdown
                    value={selectedManagerUuid}
                    onChange={(e) => {
                      setSelectedManagerUuid(e.target.value);
                    }}
                    renderInPlace={true}
                  >
                    <option value="">Do not reassign drivers</option>
                    {managers.map((manager) => (
                      <option key={manager.uuid} value={manager.uuid}>
                        {manager.name}
                        {manager.accountName && ` (${manager.accountName})`}
                      </option>
                    ))}
                  </SelectDropdown>
                  <p className="text-xs">
                    If not reassigned, drivers will keep their current manager
                    assignment.
                  </p>
                </div>
              )}

            {/* Confirmation Message Section:
                Final confirmation prompt before proceeding with deactivation. */}
            <p className="text-sm">
              Deactivating this user will block their access to the site. Are
              you sure you want to proceed?
            </p>
          </div>

          {/* Dialog Action Buttons:
              Cancel - closes the dialog without making changes
              Deactivate - confirms and proceeds with user deactivation */}
          <DialogFooter>
            <Button
              variant="outline-secondary"
              size="medium"
              onClick={handleCancelDeactivate}
            >
              Cancel
            </Button>
            <Button
              variant="outline-danger"
              size="medium"
              onClick={handleConfirmDeactivate}
            >
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate Confirmation Dialog */}
      <Dialog
        open={showReactivateDialog}
        onOpenChange={setShowReactivateDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reactivate User</DialogTitle>
            <DialogDescription className="mt-2">
              Are you sure you want to reactivate this user? They will be able
              to log in and access the system again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline-secondary"
              size="medium"
              onClick={handleCancelReactivate}
            >
              Cancel
            </Button>
            <Button
              variant="basic-primary"
              size="medium"
              onClick={handleConfirmReactivate}
            >
              Reactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
